import { isNullable } from '@ipfabric/utils';
import { toggleConfirmDialog } from 'containers/ConfirmDialog/actions';
import { take, takeEvery } from 'containers/ErrorBoundary';
import { UPLOAD_CA } from 'containers/FileUploadOverlay/constants';
import { closeDialogue } from 'containers/LeaveDialogue/actions';
import { Map as ImmutableMap, List } from 'immutable';
import get from 'lodash/get';
import { pipe } from 'ramda';
import { push } from 'redux-first-history';
import { setSubmitFailed, startSubmit, stopSubmit } from 'redux-form/immutable';
import { all, call, put, select } from 'redux-saga/effects';
import { displayMessage } from 'utils/request/displayMessage';
import { requestForSaga } from 'utils/request/request';
import { requestCustomErrorMessage } from 'utils/request/requestCustomErrorMessage';
import {
  CONFIRM_DELETE_ALL_ROLE_ASSIGNMENTS,
  DELETE_CONFIGURATION,
  DELETE_ROLE_ASSIGNMENT_DIALOG_NAME,
  FETCH_CA,
  FETCH_DATA,
  FETCH_ROLES,
  GROUP_SEARCH_DATA_CHANGE,
  NEW_ROLE_ITEM,
  PAGE_URL,
  SAVE_FORM,
  SAVE_GROUP_ERRORS,
} from './constants';
import { configFormName, roleAssignmentFormName } from './formName';

const LDAP_ERRORS = [
  'API_INVALID_LDAP_DOMAIN',
  'API_LDAP_ACCOUNT_DISABLED',
  'API_LDAP_ACCOUNT_EXPIRED',
  'API_LDAP_ACCOUNT_LOCKED_OUT',
  'API_LDAP_CONSTRAINT_VIOLATION',
  'API_LDAP_INVALID_CREDENTIALS',
  'API_LDAP_INVALID_GROUP',
  'API_LDAP_INVALID_LOGON_HOURS',
  'API_LDAP_INVALID_WORKSTATION',
  'API_LDAP_NOT_REACHABLE',
  'API_LDAP_PASSWORD_EXPIRED',
  'API_LDAP_PASSWORD_MUST_CHANGE',
  'API_LDAP_SEARCH_FAILED',
  'API_MISSING_LDAP_DOMAIN',
];

const getRequestData = (configuration) => {
  return pipe(
    (conf) => {
      if (conf.get('roleAssignments')) {
        return conf.update('roleAssignments', (roleAssignments) =>
          roleAssignments
            .map((roleAssignment, index) =>
              index === 0
                ? roleAssignment.set('groupName', 'any')
                : roleAssignment,
            )
            .map((roleAssignment) =>
              roleAssignment.get('roleId') === '' ||
              isNullable(roleAssignment.get('roleId'))
                ? roleAssignment.set('roleId', null)
                : roleAssignment,
            ),
        );
      }
      return conf;
    },
    (conf) => {
      const serverType = conf.get('serverType');
      if (serverType === 'dns') {
        const cert = pipe(
          (c) => c.get('cert', ''),
          (certificate) =>
            certificate === '' || isNullable(certificate)
              ? new List()
              : new List(certificate.split(';')),
        )(conf);
        const domain = conf.get('domain') || '';
        const serviceName = conf.get('serviceName' || '');
        const useLdaps = conf.get('useLdaps');
        return conf.set(
          'server',
          new ImmutableMap({
            cert,
            domain,
            serviceName,
            useLdaps: isNullable(useLdaps) ? false : useLdaps,
          }),
        );
      }
      return conf;
    },
    (conf) => {
      if (conf.get('server')) {
        return conf
          .delete('cert')
          .delete('domain')
          .delete('serviceName')
          .delete('useLdaps');
      }
      return conf;
    },
  )(configuration);
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy code
export function* saveFormSaga(action) {
  const { values, id, step, returnUrl } = action.payload;
  let payloadValues = values;
  const newConfiguration = id === 'new';
  const formName = `${step === 'roleAssignments' ? roleAssignmentFormName : configFormName}_${id}`;
  try {
    yield put(startSubmit(formName));
    let newConfigurationResult = null;
    if (newConfiguration) {
      const result = yield call(requestForSaga, {
        url: '/settings/ldap',
        method: 'POST',
        data: getRequestData(values.delete('id')),
      });
      newConfigurationResult = values.set('id', result.id);
      yield put({
        type: `${SAVE_FORM}_SUCCESS`,
        payload: { configuration: newConfigurationResult },
      });
      yield call(
        displayMessage,
        'success',
        'administrationLdapSettings.request.ldapSettingsSaved',
      );
    } else {
      const originalConfigurations = yield select((s) =>
        s.getIn(['administrationLdapSettings', 'configuration']),
      );
      const configId = values.get('id');
      const foundOriginal = originalConfigurations.find(
        (original) => original.get('id') === configId,
      );
      if (!foundOriginal) {
        yield put(stopSubmit(formName));
        return;
      }
      const transformedConfig = getRequestData(values);
      const transformedOriginalConfig = getRequestData(foundOriginal);
      const filteredConfig = transformedConfig.reduce((acc, value, key) => {
        if (key === 'id') {
          return acc.set(key, value);
        }
        if (List.isList(value) || ImmutableMap.isMap(value)) {
          if (!value.equals(transformedOriginalConfig.get(key))) {
            return acc.set(key, value);
          }
          return acc;
        }
        if (value !== transformedOriginalConfig.get(key)) {
          return acc.set(key, value);
        }
        return acc;
      }, new ImmutableMap());
      if (filteredConfig.size > 1) {
        let updateResult = null;
        try {
          updateResult = yield call(requestForSaga, {
            url: `/settings/ldap/${filteredConfig.get('id')}`,
            method: 'PATCH',
            data: filteredConfig.delete('id'),
          });
        } catch (e) {
          if (
            get(e, 'code') === 'API_LDAP_INVALID_GROUP' &&
            step === 'config'
          ) {
            yield put(stopSubmit(formName));
            yield put(toggleConfirmDialog(DELETE_ROLE_ASSIGNMENT_DIALOG_NAME));
            yield take(CONFIRM_DELETE_ALL_ROLE_ASSIGNMENTS);
            yield put(startSubmit(formName));
            const currentRoleAssignments =
              payloadValues.get('roleAssignments') || new List();
            const newRoleAssignments = pipe(
              (roleAssignments) => {
                const data = get(e, ['payload', 'response', 'data']);
                const invalidIndexes = data.reduce(
                  (acc, item) => acc.concat(new List(item.path)),
                  new List(),
                );
                const remainingRoleAssignments = roleAssignments.reduce(
                  (acc, roleAssignment, index) =>
                    invalidIndexes.indexOf(String(index)) > -1
                      ? acc
                      : acc.push(roleAssignment),
                  new List(),
                );
                return remainingRoleAssignments;
              },
              (roleAssignments) =>
                !roleAssignments.size
                  ? roleAssignments.push(NEW_ROLE_ITEM)
                  : roleAssignments,
            )(currentRoleAssignments);
            payloadValues = payloadValues.set(
              'roleAssignments',
              newRoleAssignments,
            );
            yield call(requestForSaga, {
              url: `/settings/ldap/${filteredConfig.get('id')}`,
              method: 'PATCH',
              data: filteredConfig
                .set('roleAssignments', newRoleAssignments)
                .delete('id'),
            });
            yield put(stopSubmit(formName));
          } else {
            throw e;
          }
        }
        const getPayload = () => {
          if (step === 'config') {
            return payloadValues;
          }
          return foundOriginal.set(
            'roleAssignments',
            values.get('roleAssignments'),
          );
        };
        yield put({
          type: `${SAVE_FORM}_SUCCESS`,
          payload: { configuration: getPayload() },
        });
        if (get(updateResult, 'criticalModification') === true) {
          yield call(
            displayMessage,
            'success',
            'administrationLdapSettings.request.criticalModification',
          );
        } else {
          yield call(
            displayMessage,
            'success',
            'administrationLdapSettings.request.ldapSettingsSaved',
          );
        }
      }
    }
    if (returnUrl) {
      const { state = {} } = returnUrl;
      yield put(
        push(
          { pathname: returnUrl.pathname, search: returnUrl.search },
          { ...state, canLeave: true },
        ),
      );
    } else {
      switch (step) {
        case 'config':
          yield put(
            push(
              {
                pathname: `${PAGE_URL}/${newConfiguration ? newConfigurationResult.get('id') : id}/role-assignments`,
              },
              { canLeave: true },
            ),
          );
          break;
        default:
          yield put(push({ pathname: PAGE_URL }, { canLeave: true }));
      }
    }
    yield put(stopSubmit(formName));
  } catch (e) {
    const errorCode = get(e, 'code');
    if (errorCode === 'API_LDAP_INVALID_GROUP') {
      yield put({
        type: SAVE_GROUP_ERRORS,
        payload: {
          errors: get(e, ['payload', 'response', 'data'], []).reduce(
            (acc, item) =>
              acc.merge(
                item.path.reduce(
                  (paths, fieldIndex) => paths.set(fieldIndex, item.type),
                  new ImmutableMap(),
                ),
              ),
            new ImmutableMap(),
          ),
        },
      });
    }
    const leaveDialogueOpen = yield select((s) =>
      s.getIn(['leaveDialogue', 'open']),
    );
    if (leaveDialogueOpen) {
      yield put(closeDialogue());
    }
    yield put(setSubmitFailed(formName));
    yield put(stopSubmit(formName));
    yield call(requestCustomErrorMessage, 'danger', e);
    if (!LDAP_ERRORS.indexOf(errorCode) === -1) {
      throw e;
    }
  }
}

export function* fetchDataSaga() {
  try {
    const configuration = yield call(requestForSaga, {
      url: '/settings/ldap',
      method: 'GET',
    });
    yield put({ type: `${FETCH_DATA}_SUCCESS`, payload: { configuration } });
  } catch (e) {
    yield put({ type: `${FETCH_DATA}_ERROR`, payload: {} });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

export function* uploadCASaga() {
  yield call(fetchCASaga);
}

export function* fetchCASaga() {
  try {
    const ca = yield call(requestForSaga, {
      url: '/settings/ca',
      method: 'GET',
    });
    yield put({
      type: `${FETCH_CA}_SUCCESS`,
      payload: { ca },
    });
  } catch (e) {
    yield put({ type: `${UPLOAD_CA}_ERROR` });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

export function* fetchRolesSaga() {
  try {
    const roles = yield call(requestForSaga, {
      url: '/tables/roles',
      method: 'POST',
      data: {
        columns: ['id', 'name', 'description'],
        filters: {},
      },
    });
    yield put({
      type: `${FETCH_ROLES}_SUCCESS`,
      payload: { roles: roles.data },
    });
  } catch (e) {
    yield put({ type: `${FETCH_ROLES}_ERROR` });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

export function* groupSearchDataChangeSaga(action) {
  try {
    const { value, id, index } = action.payload;
    yield put({
      type: `${GROUP_SEARCH_DATA_CHANGE}_START`,
      payload: { index },
    });
    const result = yield call(requestForSaga, {
      url: `/settings/ldap/${id}/groups${value ? `?search=${encodeURIComponent(value)}` : ''}`,
      method: 'GET',
    });
    yield put({
      type: `${GROUP_SEARCH_DATA_CHANGE}_SUCCESS`,
      payload: { result },
    });
  } catch (e) {
    yield put({ type: `${GROUP_SEARCH_DATA_CHANGE}_ERROR` });
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

export function* deleteConfigurationSaga(action) {
  const id = action.payload;
  try {
    yield call(requestForSaga, {
      url: `/settings/ldap/${id}`,
      method: 'DELETE',
    });
    yield put({ type: `${DELETE_CONFIGURATION}_SUCCESS`, payload: { id } });
    yield put(push({ pathname: PAGE_URL }, { canLeave: true }));
  } catch (e) {
    yield call(requestCustomErrorMessage, 'danger', e);
    throw e;
  }
}

export function* defaultSaga() {
  yield all([
    takeEvery(SAVE_FORM, saveFormSaga),
    takeEvery(FETCH_DATA, fetchDataSaga),
    takeEvery(`${UPLOAD_CA}_SUCCESS`, uploadCASaga),
    takeEvery(FETCH_CA, fetchCASaga),
    takeEvery(GROUP_SEARCH_DATA_CHANGE, groupSearchDataChangeSaga),
    takeEvery(DELETE_CONFIGURATION, deleteConfigurationSaga),
    takeEvery(FETCH_ROLES, fetchRolesSaga),
  ]);
}

export default defaultSaga;
